import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Table } from 'react-bootstrap';
import { FaCopy, FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';
import TestService from '../../../services/testService';

const QuestionImportModal = ({ show, onHide, targetCategoryId, onSuccess }) => {
    const [sourceCategoryId, setSourceCategoryId] = useState('');
    const [categories, setCategories] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [selectedQuestions, setSelectedQuestions] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (show) {
            loadCategories();
            setSelectedQuestions({});
            setSourceCategoryId('');
        }
    }, [show]);

    useEffect(() => {
        if (sourceCategoryId) {
            loadQuestions();
        }
    }, [sourceCategoryId, searchTerm]);

    const loadCategories = async () => {
        try {
            setLoading(true);
            const response = await TestService.getCategories();
            const allCategories = response.data || [];
            // Filter out target category
            setCategories(allCategories.filter(c => c.id !== targetCategoryId));
        } catch (error) {
            console.error('Failed to load categories:', error);
            toast.error('Failed to load categories');
        } finally {
            setLoading(false);
        }
    };

    const loadQuestions = async () => {
        try {
            setLoadingQuestions(true);
            const response = await TestService.getQuestions(sourceCategoryId, 1, 100, searchTerm);
            setQuestions(response.data?.data || []);
            setSelectedQuestions({});
        } catch (error) {
            console.error('Failed to load questions:', error);
            toast.error('Failed to load questions');
        } finally {
            setLoadingQuestions(false);
        }
    };

    const toggleSelectAll = () => {
        if (Object.keys(selectedQuestions).length === questions.length) {
            setSelectedQuestions({});
        } else {
            const newSelected = {};
            questions.forEach(q => {
                newSelected[q.id] = true;
            });
            setSelectedQuestions(newSelected);
        }
    };

    const toggleSelectQuestion = (questionId) => {
        setSelectedQuestions(prev => ({
            ...prev,
            [questionId]: !prev[questionId]
        }));
    };

    const handleImport = async () => {
        const selectedIds = Object.keys(selectedQuestions).filter(key => selectedQuestions[key]);
        
        if (selectedIds.length === 0) {
            toast.warning('Please select at least one question to import');
            return;
        }
        
        if (!sourceCategoryId) {
            toast.warning('Please select a source category');
            return;
        }
        
        try {
            setSubmitting(true);
            // API call to import questions
            const response = await TestService.importQuestions({
                source_category_id: sourceCategoryId,
                target_category_id: targetCategoryId,
                question_ids: selectedIds.map(id => parseInt(id))
            });
            
            toast.success(`${response.data?.imported || selectedIds.length} questions imported successfully`);
            onSuccess();
            onHide();
        } catch (error) {
            console.error('Failed to import questions:', error);
            toast.error(error.response?.data?.message || 'Failed to import questions');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FaCopy className="me-2 text-primary" />
                    Import Questions from Another Category
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="info">
                    <strong>How it works:</strong> Select questions from another category to copy to the current category.
                    All question details (including options, marks, and negative marking settings) will be duplicated.
                </Alert>
                
                <Form.Group className="mb-3">
                    <Form.Label>Source Category *</Form.Label>
                    <Form.Select
                        value={sourceCategoryId}
                        onChange={(e) => setSourceCategoryId(e.target.value)}
                        disabled={loading}
                    >
                        <option value="">-- Select a category to import from --</option>
                        {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>
                                {cat.name} ({cat.total_questions || 0} questions)
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
                
                {sourceCategoryId && (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Search Questions</Form.Label>
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by question..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <button className="btn btn-outline-secondary" onClick={loadQuestions}>
                                    <FaSearch />
                                </button>
                            </div>
                        </Form.Group>
                        
                        {loadingQuestions ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" size="sm" />
                                <p className="mt-2">Loading questions...</p>
                            </div>
                        ) : questions.length === 0 ? (
                            <Alert variant="warning" className="text-center">
                                No questions found in this category.
                            </Alert>
                        ) : (
                            <>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <strong>{questions.length} questions available</strong>
                                    <Button variant="outline-secondary" size="sm" onClick={toggleSelectAll}>
                                        {Object.keys(selectedQuestions).length === questions.length ? 'Deselect All' : 'Select All'}
                                    </Button>
                                </div>
                                
                                <div className="border rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    <Table striped hover size="sm" className="mb-0">
                                        <thead className="sticky-top bg-white">
                                            <tr>
                                                <th style={{ width: '50px' }}>Select</th>
                                                <th>Question</th>
                                                <th style={{ width: '80px' }}>Marks</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {questions.map((q, idx) => (
                                                <tr key={q.id}>
                                                    <td className="text-center">
                                                        <Form.Check
                                                            type="checkbox"
                                                            checked={selectedQuestions[q.id] || false}
                                                            onChange={() => toggleSelectQuestion(q.id)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <div dangerouslySetInnerHTML={{ 
                                                            __html: q.question.length > 100 
                                                                ? q.question.substring(0, 100) + '...' 
                                                                : q.question 
                                                        }} />
                                                    </td>
                                                    <td>{q.marks}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                
                                <div className="mt-2 text-muted small">
                                    Selected: {Object.keys(selectedQuestions).filter(k => selectedQuestions[k]).length} questions
                                </div>
                            </>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={submitting}>
                    Cancel
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleImport} 
                    disabled={submitting || !sourceCategoryId || Object.keys(selectedQuestions).filter(k => selectedQuestions[k]).length === 0}
                >
                    {submitting ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Importing...
                        </>
                    ) : (
                        <>
                            <FaCopy className="me-2" />
                            Import Selected ({Object.keys(selectedQuestions).filter(k => selectedQuestions[k]).length})
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default QuestionImportModal;